/**
 * @file 帖子列表接口
 * @author Yongning 2024-04-11
 */

import { Toast } from '@/components';
import request, { MethodEnum, serverTriggerUrl } from '@/utils/fetch';
import { TopLevelChannels } from '../home';
import { IForumType } from '@/api/detail';
import { formatArticalCovers } from '@/hooks/useFormatArticalContent';

export enum RankEnum {
  vip = 1,
  admin = 2,
}

export enum ArticalCoverEnum {
  image = 1,
  video = 2,
}

export enum SortEnum {
  // 最新发布
  latestPosts = 'latest_posts',
  // 最多评论
  mostComments = 'most_comments',
  // 最多浏览
  mostViews = 'most_views',
  // 最新评论
  latestComments = 'latest_comments',
  // 最热
  hottest = 'hottest',
  // 导入顺序
  importOrder = 'import_order',
}

// 用户关联设备
type IArticalAutherDevice = {
  key: number;
  name: string;
  home_page: string;
  icon: string;
};

// 用户组
type IArticalAutherGroup = {
  title: string;
  icon: string;
};

// 用户头衔
type IArticalAutherRank = {
  vname: string;
  vpic: string;
  vid: RankEnum;
};

// 帖子关联用户
export type IArticalAuther = {
  id: number;
  uuid: string;
  name: string;
  avatar: string;
  device?: IArticalAutherDevice;
  group: IArticalAutherGroup;
  rank?: IArticalAutherRank;
  total_distance: number;
  hide_distance?: boolean;
};

// 帖子封面
export type IArticalCover = {
  extra_large: string;
  large: string;
  medium: string;
  square_360_360: string;
  small?: string;
  type: ArticalCoverEnum;
};

export type IChannelTagType = {
  id: number;
  slug: string;
  name: string;
  display_type: string;
  channel_path: string[];
}

export type IThreadLisItem = {
  tid: number;
  author: IArticalAuther;
  // 帖子标题
  subject: string;
  // 帖子摘要
  summary?: string;
  covers?: IArticalCover[];
  devices?: IArticalAutherDevice[];
  // 是否精华
  digest: boolean;
  // 是否收藏
  favorite: boolean;
  // 收藏次数
  favtimes: number;
  // 是否热贴
  hotpost: boolean;
  // 是否新贴
  newpost: boolean;
  // 是否点赞
  praised: boolean;
  // 点赞数量
  praise: number;
  // 回帖数量
  replies: number;
  // 阅读数量
  views: number;
  // 最新回复时间
  lastpost: number;
  // 帖子创建时间
  dateline: number;
  // 主题及板块信息
  forum_types: IForumType[];
  // 标签
  forum_channel_tags: IChannelTagType[];
};

export type IThreadListReq = {
  channel_slug?: string;
  limit?: number;
  offset?: number;
  sort_type?: string;
};

export interface IThreadListRese {
  items: IThreadLisItem[];
}

export const LIMIT = 10;

const getThreadList = (
  {
    offset = 0,
    limit = LIMIT,
    sort_type = SortEnum.hottest,
    channel_slug = TopLevelChannels.home_page_recommended_channel,
  }: IThreadListReq,
  isServerTrigger = false,
  headers = {},
): Promise<IThreadListRese['items']> => {
  const key = 'GetThreadList';
  const prefix = isServerTrigger ? serverTriggerUrl : '';

  return request({
    url: `${prefix}/forum/thread/list`,
    method: MethodEnum.GET,
    key,
    isServerTrigger,
    headers,
    params: {
      channel_slug,
      limit,
      offset,
      sort_type,
    },
  }).then((data: IThreadListRese) => {
    const result = (data?.items || []).map(({ covers = [], ...rest }) => {
      return {
        covers: formatArticalCovers(covers),
        ...rest,
      }
    });
    return result;
  }).catch(err => {
     if (!isServerTrigger) {
      Toast.show({
        content: err.message,
      });
     }
    return Promise.reject(err);
  })
};

export default getThreadList;
